import { useEffect, useRef } from 'react';
import { Routes, Route, useLocation, useNavigationType, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useAuth } from '@/stores/RootStore';
import { useLenis, getLenis } from '@/hooks/useLenis';
import { recordVisit } from '@/services/visit';
import Header from '@/layout/Header';
import Footer from '@/layout/Footer';
import ChatWidget from '@/components/ChatWidget';
import Home from '@/pages/Home';
import UpdateNotice from '@/components/UpdateNotice'; // [2026-10-23 사용자] 전역 공지(모든 페이지). 클릭 통과·닫기 기억.
import Company from '@/pages/Company';
import CompanyCertifications from '@/pages/CompanyCertifications';
// [2026-10-08] 기획서 v7 8: 회사소개 분화 독립 URL(연혁·조직도·인재상채용·연구개발·오시는길)
import { CompanyHistory, CompanyOrganization, CompanyCareers, CompanyRd, CompanyLocation } from '@/pages/CompanySubpages';
import Igm from '@/pages/Igm';
import Install from '@/pages/Install';
import Products from '@/pages/Products';
import Sitemap from '@/pages/Sitemap'; // [2026-10-11 사용자] 사이트맵 신규 페이지
// [2026-10-08] 기획서 v7 — 제품별 독립 상세페이지 1호(EVS-G200). 문서 2-2 10섹션 + 반복 CTA.
import ProductEvsG200 from '@/pages/ProductEvsG200';
import ProductInstruments from '@/pages/ProductInstruments';
import ProductHeatIndex from '@/pages/ProductHeatIndex';
import ProductDisplayRepeater from '@/pages/ProductDisplayRepeater';
import SafetyPlatform from '@/pages/SafetyPlatform';
// [2026-10-08] 기획서 v7 3-1·4-1: 도메인 랜딩을 문서 섹션 순서로 재구성(전용 컴포넌트). DomainPage(구 공용)는 미사용.
import SafetyLanding from '@/pages/SafetyLanding';
import SafetyCold from '@/pages/SafetyCold';
import SafetyGas from '@/pages/SafetyGas';
import SafetyHeat from '@/pages/SafetyHeat';
import EnvironmentLanding from '@/pages/EnvironmentLanding';
import EnvironmentGreenlink from '@/pages/EnvironmentGreenlink';
import DomainPage from '@/pages/DomainPage';
import GuidePage from '@/pages/GuidePage';
import { SAFETY_PAGE, ENVIRONMENT_PAGE } from '@/data/mockData';
import Support from '@/pages/Support';
import NewsList from '@/pages/NewsList';
import NewsDetail from '@/pages/NewsDetail';
import CasesList from '@/pages/CasesList';
import CaseDetail from '@/pages/CaseDetail';
import CasesClients from '@/pages/CasesClients'; // [2026-10-08] 기획서 v7 6-3 주요거래처·실적
// [2026-10-08 B안] 설치안내=도입프로세스 재정의(/install/*) + /business/install=설치사업
import { InstallProcess, InstallSiteSurvey, InstallOperation, BusinessInstall } from '@/pages/InstallPages';
// [2026-09-10] 회원 인증 페이지(P4) — 로그인·회원가입·찾기·마이페이지·관리자
import Login from '@/pages/auth/Login';
import Signup from '@/pages/auth/Signup';
import FindAccount from '@/pages/auth/FindAccount';
import MyPage from '@/pages/auth/MyPage';
import AdminHome from '@/pages/auth/AdminHome';
import SocialCallback from '@/pages/auth/SocialCallback';
import SocialComplete from '@/pages/auth/SocialComplete';
import InquiryForm from '@/pages/support/InquiryForm';
import MyInquiries from '@/pages/support/MyInquiries';
import AsCenter from '@/pages/support/AsCenter';
import AsForm from '@/pages/support/AsForm';
import MyAs from '@/pages/support/MyAs';
import Faq from '@/pages/support/Faq';
import Consult from '@/pages/support/Consult';
import Resources from '@/pages/support/Resources';

// 뒤로가기 스크롤 복원용 — location.key별 마지막 스크롤 위치 저장(세션 내 모듈 스코프).
//   [[evs-back-restores-scroll]]: PUSH(새 클릭 진입)=최상단 / POP(뒤로·앞으로)=이전 위치 복원.
//   예) 홈 하단 '뉴스&소식' 카드 클릭 → 상세 → 뒤로가기 = 홈 최상단이 아니라 그 뉴스 섹션 위치로 복귀.
const scrollPositions = new Map<string, number>();

// 라우트 이동 시 스크롤 처리(2026-09-08 사용자 요청).
//   단 '#앵커' 이동(회사소개 인사말·조직도 등)은 각 페이지가 해당 섹션으로 스크롤해야 하므로 제외.
function ScrollToTop() {
  const location = useLocation();
  const navType = useNavigationType(); // 'PUSH' | 'REPLACE' | 'POP'
  const keyRef = useRef(location.key);
  keyRef.current = location.key; // 렌더마다 현재 라우트 key 반영(스크롤 저장에 사용)
  const prevPath = useRef(location.pathname); // [2026-09-13] 직전 경로 추적(같은 탭영역 내 이동 감지)

  // 스크롤 위치 실시간 저장(전역 리스너 1회). 핵심: 라우트 전환 시 DOM이 짧은 페이지로 교체되며
  //   window.scrollY가 그 페이지 최대치로 '클램프'되어 잘못 저장되던 문제 → 콘텐츠 높이가 급감하면
  //   (전환 클램프로 판단) 그 스크롤 이벤트는 저장 스킵. 사용자가 스크롤한 마지막 정상값이 보존됨.
  useEffect(() => {
    let lastH = document.documentElement.scrollHeight;
    const onScroll = () => {
      const h = document.documentElement.scrollHeight;
      if (h < lastH - 200) { lastH = h; return; } // 페이지 급격히 짧아짐 = 라우트 전환 클램프 → 저장 안 함
      lastH = h;
      scrollPositions.set(keyRef.current, window.scrollY);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const prev = prevPath.current; prevPath.current = location.pathname;
    if (location.hash) return; // 앵커 이동은 예외(useLenis.scrollToSection 이 처리)
    // [2026-09-13 사용자] 같은 탭 페이지(/mypage) 내 탭 이동은 스크롤 리셋 예외 — 탭 클릭 시 상단으로 튀는 불편 제거.
    //   (관리자 /admin 탭은 setTab 상태전환이라 애초에 라우트 전환 없음 → 여기 안 걸림.)
    const seg = (p: string) => p.split('/')[1] || '';
    if (navType !== 'POP' && seg(location.pathname) === 'mypage' && seg(prev) === 'mypage') return;
    // 목표: POP(뒤로/앞으로)면 저장된 이전 스크롤 복원, 새 진입(PUSH/REPLACE)이면 최상단(0).
    const target = navType === 'POP' ? (scrollPositions.get(location.key) ?? 0) : 0;
    // html에 scroll-behavior:smooth + Lenis 관성이 걸려 있어 단발 리셋은 중간(≈180px)에 멈출 수 있고,
    //   전환 직후 ~19ms(다음 Lenis 프레임)에 Lenis가 stale 스크롤을 한 번 되씀 → 진입 로더가 가리는
    //   짧은 구간(≈420ms) 동안 매 프레임 target으로 강제(CSS smooth 우회 + Lenis force).
    const htmlEl = document.documentElement;
    const isRestore = navType === 'POP' && target > 0; // 뒤로가기로 큰 위치 복원
    const start = performance.now();
    let raf = 0;
    const pin = () => {
      const prev = htmlEl.style.scrollBehavior;
      htmlEl.style.scrollBehavior = 'auto';
      if (Math.abs(window.scrollY - target) > 1) window.scrollTo(0, target);
      getLenis()?.scrollTo(target, { immediate: true, force: true });
      htmlEl.style.scrollBehavior = prev;
      const elapsed = performance.now() - start;
      // 복원(POP): 돌아온 페이지가 점진 렌더로 아직 높이가 모자라 target에 클램프될 수 있음 →
      //   target에 도달할 때까지(±4px) 매 프레임 재적용(최대 1.6s). 도달하면 즉시 해제(스크롤 자유).
      //   새 진입(PUSH/top): 로더 구간(≈420ms) 동안만 top 고정(Lenis stale 방어).
      const reached = Math.abs(window.scrollY - target) <= 4;
      // 복원은 이미지 로드로 페이지 높이가 늦게 커질 수 있어(특히 느린 네트워크) 재적용 창을 넉넉히(2.5s).
      const keep = isRestore ? (!reached && elapsed < 2500) : (elapsed < 420);
      if (keep) raf = requestAnimationFrame(pin);
    };
    pin();
    // 저장은 위 스크롤 리스너가 실시간 처리(전환 클램프값 저장 방지). 여기선 pin 정리만.
    return () => cancelAnimationFrame(raf);
  }, [location.key]); // key는 이동마다 갱신(PUSH=새 key, POP=이전 key 재사용)
  return null;
}

// [2026-09-11] 방문 추적 — 라우트 전환 시 '직전' 페이지의 체류시간(dwell)을 기록, 이탈(pagehide) 시 마지막 페이지 flush.
//   로그인 회원만 기록(recordVisit이 토큰 없으면 no-op). 관리자 ERP 대시보드의 체류·방문빈도·기기 동향 근거.
function VisitTracker() {
  const location = useLocation();
  const prev = useRef<{ path: string; t: number } | null>(null);
  useEffect(() => {
    const now = Date.now();
    if (prev.current) recordVisit(prev.current.path, now - prev.current.t);
    prev.current = { path: location.pathname, t: now };
  }, [location.pathname]);
  useEffect(() => {
    const flush = () => { if (prev.current) recordVisit(prev.current.path, Date.now() - prev.current.t); };
    window.addEventListener('pagehide', flush);
    return () => window.removeEventListener('pagehide', flush);
  }, []);
  return null;
}

// [2026-09-12 사용자] 소셜 가입 완료(휴대폰 인증) 강제 — 전역 가드.
//   WHY: 소셜 신규가입은 status=PENDING. 완료단계(/auth/complete)를 마치기 전에는 어떤 페이지로 가도
//        항상 완료페이지로 되돌린다. 기존엔 SocialCallback의 1회성 redirect뿐이라, 사용자가 직접 URL 이동하거나
//        탭 닫았다 재접속(localStorage 토큰 → /me 복원)하면 PENDING인 채 사이트를 쓸 수 있었음.
//   서버측 하드 강제는 JwtAuthInterceptor의 PENDING 게이트(보호 API 403)와 짝. 이건 UX(화면 유도) 쪽.
//   auth.ready(세션 복원 완료) 후에만 판단 — 복원 전 status 미확정으로 오판/깜빡임 방지.
const PendingGuard = observer(function PendingGuard() {
  const auth = useAuth();
  const location = useLocation();
  const nav = useNavigate();
  useEffect(() => {
    if (!auth.ready) return;
    if (auth.user?.status === 'PENDING' && location.pathname !== '/auth/complete') {
      nav('/auth/complete', { replace: true });
    }
  }, [auth.ready, auth.user?.status, location.pathname, nav]);
  return null;
});

// 앱 셸 — Header/Footer/Lenis는 라우트 공통, 본문만 라우팅.
//   '/'        = 홈(메인 섹션들)
//   '/company' = 회사소개 단일 서브페이지(인사말·조직도… 를 앵커 섹션으로. 자식 클릭 시 부드럽게 스크롤)
export default function App() {
  useLenis();
  // 브라우저 네이티브 스크롤 복원 비활성화(2026-09-08) — auto로 두면 뒤로가기 시 브라우저가
  //   기억한 위치(예: 3978px)로 되돌려 우리의 복원 로직과 충돌(항상 그 고정값으로 튐).
  //   manual로 해야 ScrollToTop이 저장된 정확한 위치를 완전히 제어. [[evs-back-restores-scroll]]
  useEffect(() => {
    if ('scrollRestoration' in history) {
      const prev = history.scrollRestoration;
      history.scrollRestoration = 'manual';
      return () => { history.scrollRestoration = prev; };
    }
  }, []);
  return (
    <>
      <ScrollToTop />
      <VisitTracker />
      <PendingGuard />
      <Header />
      <UpdateNotice />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/company" element={<Company />} />
        <Route path="/sitemap" element={<Sitemap />} />
        <Route path="/company/certifications" element={<CompanyCertifications />} />
        <Route path="/company/history" element={<CompanyHistory />} />
        <Route path="/company/organization" element={<CompanyOrganization />} />
        <Route path="/company/careers" element={<CompanyCareers />} />
        <Route path="/company/rd" element={<CompanyRd />} />
        <Route path="/company/location" element={<CompanyLocation />} />
        <Route path="/business/igm" element={<Igm />} />
        <Route path="/business/install" element={<BusinessInstall />} /> {/* [2026-10-08 B] 설치사업(기획서 4-3). 구 Install(시설콘텐츠)은 /guide로 일원화 */}
        <Route path="/install/process" element={<InstallProcess />} />
        <Route path="/install/site-survey" element={<InstallSiteSurvey />} />
        <Route path="/install/operation" element={<InstallOperation />} />
        <Route path="/products" element={<Products />} />
        <Route path="/products/evs-g200" element={<ProductEvsG200 />} />
        <Route path="/products/instruments" element={<ProductInstruments />} />
        <Route path="/products/heat-index" element={<ProductHeatIndex />} />
        <Route path="/products/display-repeater" element={<ProductDisplayRepeater />} />
        {/* 도메인 기본 페이지(2026-09-08) — 데이터 주도 DomainPage 재사용. 서브메뉴 앵커(/safety#saas 등) 대상. */}
        <Route path="/safety" element={<SafetyLanding />} />
        <Route path="/safety/platform" element={<SafetyPlatform />} />
        <Route path="/safety/heat" element={<SafetyHeat />} /> {/* [2026-10-11] 기획서 v7 3-3 온열질환 예방(안전 시나리오). 구 /safety#thermo·제품연결에서 독립 페이지로 */}
        <Route path="/safety/cold" element={<SafetyCold />} />
        <Route path="/safety/gas" element={<SafetyGas />} />
        <Route path="/environment" element={<EnvironmentLanding />} />
        <Route path="/environment/greenlink" element={<EnvironmentGreenlink />} />
        {/* 설치안내 — 단일 앵커 페이지(2026-09-08). 제조업·방지시설·배출시설·설치대상 섹션. 서브메뉴=/guide#id. */}
        <Route path="/guide" element={<GuidePage />} />
        <Route path="/support" element={<Support />} />
        {/* 뉴스 & 소식 — 목록 + 상세(블로그 캡처 기반). D안 작업 순서상 우선 구현(2026-09-07) */}
        <Route path="/news" element={<NewsList />} />
        <Route path="/news/:slug" element={<NewsDetail />} />
        {/* [2026-09-09 사용자] 설치 사례(도입사례) — 설치안내와 다른 신규 페이지 */}
        <Route path="/cases" element={<CasesList />} />
        <Route path="/cases/clients" element={<CasesClients />} />
        <Route path="/cases/:slug" element={<CaseDetail />} />
        {/* 회원 인증(P4) */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/find-account" element={<FindAccount />} />
        <Route path="/mypage" element={<MyPage />} />
        <Route path="/mypage/:tab" element={<MyPage />} />
        <Route path="/admin" element={<AdminHome />} />
        <Route path="/auth/callback/:provider" element={<SocialCallback />} />
        {/* [2026-09-11 #4] 소셜 신규가입 완료단계(전화인증·약관·유효기간). SocialCallback이 needsCompletion 시 라우팅 */}
        <Route path="/auth/complete" element={<SocialComplete />} />
        <Route path="/support/inquiry" element={<InquiryForm />} />
        <Route path="/support/my-inquiries" element={<MyInquiries />} />
        <Route path="/support/as" element={<AsCenter />} />
        <Route path="/support/as/apply" element={<AsForm />} />
        <Route path="/support/my-as" element={<MyAs />} />
        <Route path="/support/faq" element={<Faq />} />
        <Route path="/support/consult" element={<Consult />} />
        <Route path="/support/resources" element={<Resources />} />
      </Routes>
      <Footer />
      {/* 실시간 상담 위젯(D안 Phase 2-③) — 전 페이지 우하단 상시 */}
      <ChatWidget />
    </>
  );
}
